import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";

import FilterPopover from "./FilterPopover";
import { FilterField, FilterRendererProps } from "./types";
import BaseButton from "../Button/BaseButton";
import { FilterKey } from "@/views/datablocks/types/types";

const FilterRenderer = <T,>({
  filters,
  activeFilterNames,
  onFiltersChange,
  form,
  activeFilterCount,
  popoverTitle,
  handleClear,
  handleSearch,
  areFiltersDirty,
  className,
}: FilterRendererProps<T>) => {
  const { t } = useTranslation();

  const isClearingFilters = useRef(false);
  const debouncedFiltersChange = useMemo(
    () => _.debounce((filters) => onFiltersChange(filters), 500),
    [onFiltersChange],
  );

  const activeFields = useMemo(() => {
    return activeFilterNames.reduce<FilterField[]>((acc, filterName) => {
      const field = filters[filterName as FilterKey];
      if (field) {
        acc.push(field);
      }
      return acc;
    }, []);
  }, [activeFilterNames, filters]);

  const popoverFields = activeFields.filter((i) => i.isAdvancedFilter);
  const regularFields = activeFields.filter((i) => !i.isAdvancedFilter);

  useEffect(() => {
    const subscription = form.watch((formValues, { name }) => {
      if (isClearingFilters.current || !name) return;
      const debouncedFields = activeFields
        .filter((field) => field?.isDebounced)
        .map((field) => field.name);
      const nonAdvancedFilters = activeFields
        .filter((field) => !field?.isAdvancedFilter)
        .map((field) => field.name);
      if (debouncedFields.includes(name)) {
        debouncedFiltersChange(formValues);
      } else if (nonAdvancedFilters.includes(name)) {
        onFiltersChange(formValues);
      }
    });

    return () => {
      subscription.unsubscribe();
      debouncedFiltersChange.cancel();
    };
  }, [form, activeFields, onFiltersChange]);

  const ClearAllButton = () => (
    <BaseButton variant="link" onClick={handleClear}>
      {t("buttons.clearFilters")}
    </BaseButton>
  );

  const showClearButton = (index: number) => {
    const noAdvancedFilters = popoverFields.length === 0;
    return areFiltersDirty && noAdvancedFilters && index === 0;
  };

  return (
    <div className={`${className || ""}`}>
      {regularFields.map((field, index) => (
        <span
          key={`${field.label}-${index}`}
          className="filter-renderer-container"
        >
          {field.component}
          {showClearButton(index) && <ClearAllButton />}
        </span>
      ))}

      {popoverFields.length > 0 && (
        <FilterPopover
          title={popoverTitle || t("buttons.filters")}
          fields={popoverFields}
          activeFilterCount={activeFilterCount}
          handleClear={() => {
            isClearingFilters.current = true;
            handleClear();
            isClearingFilters.current = false;
          }}
          handleSearch={handleSearch}
        />
      )}
    </div>
  );
};

export default FilterRenderer;
